import { useAppContext } from "@context/app";
import { useUI } from "@context/ui-managed-context";
import Button from "@elements/button";
import NewAddress from "@modules/address";
import Modal from "@modules/layout/modal";
import PathIcon from "@svg/PathIcon";
import WarningIcon from "@svg/WarningIcon";
import { BiPencil } from "react-icons/bi";
import { CartItemModel, CartStep, IAddressFieldsInput } from "@types";
import { COOKIE_AREA_KEY } from "@utils/constants";
import { getCookie } from "@utils/Cookies";
import { convertNumber } from "@utils/digits";
import { commonApi, userApi } from "gate";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import SendWithoutAddress from "./sendWithoutAddress";
import Loading from "assets/lottie/loading";
import AddressesIcon from "@svg/AddressesIcon";

interface AddressProps {
  setIsLoading: (value: boolean) => void;
  changeStep: (value: CartStep) => void;
  action: (value: {
    next: CartStep;
    current: CartStep;
    callBack?: (data: CartItemModel[]) => void;
  }) => void;
}

const Address: React.FC<AddressProps> = ({ changeStep, action }) => {
  const t = useTranslations();
  const { locale } = useRouter();
  const {
    user,
    setUserDeliveryAddress,
    deliveryAddress,
    oldDeliveryAddress,
    setUserOldDeliveryAddress,
    country,
    setUserArea,
  } = useAppContext();
  const { setModalView, openModal, closeModal } = useUI();
  const [addressList, setAddressList] = useState<IAddressFieldsInput[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<
    undefined | number | "SendWithoutAddress"
  >();
  const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
  const [addressToEdit, setAddressToEdit] = useState<IAddressFieldsInput | undefined>(undefined);
  const [openReceiverInfo, setOpenReceiverInfo] = useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [orderByPhoneStatus, setOrderByPhoneStatus] = useState(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [warningDisclaimer, setWarningDisclaimer] = useState<string | undefined>(undefined);

  const Cookies = getCookie();

  useEffect(() => {
    if (user.isLoggedIn) {
      closeModal();
      getAddressList();
    } else {
      setModalView("LOGIN_VIEW", false);
      openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoggedIn]);

  useEffect(() => {
    setSelectedAddress(deliveryAddress?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAddressList = async () => {
    try {
      setIsFetching(true);
      const addresses = await userApi.dynamicAddressList().fetch();
      setAddressList(addresses.data.items);
      setOrderByPhoneStatus(addresses.data.order_by_phone_status);
      setWarningDisclaimer(addresses.data.warning_disclaimer);
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
    }
  };

  const submitAddressView = (value: IAddressFieldsInput, isGift?: boolean) => {
    getAddressList();
    setSelectedAddress(value.id);
    if (isGift) {
      handelChangeAddress(value);
    }
  };

  const handelUpdateCart = (value: CartItemModel[]) => {
    setOpenConfirmModal(true);
  };
  const handelChangeAddress = (adrs?: IAddressFieldsInput) => {
    const areaCode = Cookies.get(COOKIE_AREA_KEY);
    const target = adrs ?? addressList.find((a) => a.id === selectedAddress);
    if (target) {
      if (country && target.area_id && target.area_id !== Number(areaCode)) {
        Cookies.set(COOKIE_AREA_KEY, target.area_id, {}, 365 * 2, false, false);
        commonApi
          .getAreaList(country.id)
          .fetch()
          .then((res) => {
            const area = res.data.items.find((area) =>
              area.items.some((a) => a.id === Number(target.area_id))
            );
            if (area) {
              setUserArea(area);
            }
          })
          .catch((err) => {
            // Handle error
          });
      }
      setUserOldDeliveryAddress(deliveryAddress);
      setUserDeliveryAddress(target);
      action({ next: "gift", current: "address", callBack: handelUpdateCart });
    }
  };

  const undoAddress = () => {
    setUserDeliveryAddress(oldDeliveryAddress);
    setSelectedAddress(oldDeliveryAddress?.id);
    Cookies.set(
      COOKIE_AREA_KEY,
      oldDeliveryAddress?.area_id || deliveryAddress?.area_id,
      {},
      365 * 2,
      false,
      false
    );

    const areaId = oldDeliveryAddress?.area_id || deliveryAddress?.area_id;
    if (country && areaId) {
      commonApi
        .getAreaList(country.id)
        .fetch()
        .then((res) => {
          const area = res.data.items.find((a) => a.id === Number(areaId));
          if (area) {
            setUserArea(area);
          }
        })
        .catch((err) => {
          // Handle error
        });
    }

    setUserOldDeliveryAddress(undefined);
    setOpenConfirmModal(false);
  };

  const handleOpenNewAddress = () => {
    setAddressToEdit(undefined);
    setShowAddressForm(true);
  };

  const handleEditAddress = (address: IAddressFieldsInput) => {
    setSelectedAddress(address.id);
    setAddressToEdit(address);
    setShowAddressForm(true);
  };

  const handleCloseAddressForm = (value: boolean) => {
    setShowAddressForm(value);
    if (!value) {
      setAddressToEdit(undefined);
    }
  };

  return (
    <div className="w-full mt-10">
      {isFetching && <Loading />}
      {showAddressForm && (
        <Modal open={showAddressForm} onClose={() => handleCloseAddressForm(false)} cancelable>
          <div className="w-screen max-w-[850px] min-h-[480px] h-[max-content] bg-white rounded-lg p-4">
            <NewAddress
              action={submitAddressView}
              onClose={handleCloseAddressForm}
              data={addressToEdit}
              id={addressToEdit?.id}
            />
          </div>
        </Modal>
      )}

      {openReceiverInfo && (
        <Modal open={openReceiverInfo} onClose={() => setOpenReceiverInfo(false)} cancelable>
          <div className="md:w-full sm:w-screen xs:w-screen w-screen max-w-[432px] min-h-[363px] h-[max-content] bg-white rounded-lg p-4">
            <SendWithoutAddress
              action={(data: IAddressFieldsInput) => submitAddressView(data, true)}
              onClose={() => setOpenReceiverInfo(false)}
            />
          </div>
        </Modal>
      )}

      <Modal open={openConfirmModal} onClose={() => setOpenConfirmModal(false)} cancelable={true}>
        <div className="md:w-[480px] sm:w-screen xs:w-screen w-screen max-w-[480px] min-h-[410px] h-[max-content] bg-white rounded-lg p-4 flex flex-col justify-between">
          <div className=" w-full flex flex-wrap mb-5 items-center">
            <p className="flex-1 font-normal text-xl text-[#333] text-center">
              {t("w-productAvailability")}
            </p>
          </div>
          <div className=" flex flex-col items-center w-full">
            <WarningIcon />
            <p className=" text-[#999] font-normal text-sm text-center max-w-[340px] mt-4 leading-6">
              {t("w-productAvailabilityMsg")}
            </p>
          </div>
          <div className="w-full mt-2">
            <div className="w-full flex flex-wrap justify-center">
              <Button
                className="flex-1 max-w-52 h-12 font-extrabold text-base rounded-full m-2"
                size="slim"
                theme="lightBlue"
                onClick={() => undoAddress()}
              >
                {t("w-changeAddress")}
              </Button>
              <Button
                className="flex-1 max-w-52 h-12 font-extrabold text-base rounded-full m-2"
                size="slim"
                onClick={() => {
                  changeStep("cart");
                }}
              >
                {t("client_buttons_updatecart")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <div className=" w-full flex items-center mb-10 flex-wrap ">
        <div className="relative h-10 sm:w-1 xs:w-[100px] w-[100px] ">
          <div className=" absolute -top-2">
            <Button
              size="slim"
              className="h-10 font-normal text-sm rounded-full m-2 px-0 w-[92px]"
              theme="lightBlue"
              onClick={() => action({ next: "cart", current: "address" })}
            >
              <PathIcon
                stroke="#008AF6"
                className={` ${locale?.includes("en") ? "rotate-180" : ""}  mx-2`}
              />
              <span className="mx-2 font-normal">{t("w-back")}</span>{" "}
            </Button>
          </div>
        </div>
        <p className="text-center lg:text-[32px] md:text-2xl  text-2xl font-extrabold text-[#333333] flex-1 ">
          {t("client_address_selectaddress")}
        </p>
      </div>
      {warningDisclaimer && (
        <p className="text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 my-10">
          {warningDisclaimer}
        </p>
      )}

      {addressList.length ? (
        <>
          <div className="flex-1 w-full flex flex-col min-h-[100px]  max-h-[360px] max-w-[480px] mx-auto mt-5">
            <div className=" w-full flex-1 overflow-auto">
              {addressList.map((address) => (
                <div
                  key={address.id}
                  className={`lg:w-full md:w-full sm:w-full xs:w-full w-full max-w-[480px] mx-auto  mb-1 p-2`}
                >
                  <div
                    className={`w-full min-h-[104px] cursor-pointer transition-all ease-in-out duration-150 ${
                      selectedAddress === address.id ? " border-2 border-[#008AF6]" : "border-2"
                    }  rounded-2xl px-5 py-4`}
                    onClick={() => {
                      setSelectedAddress(address.id);
                    }}
                  >
                    <div className="flex flex-wrap items-center w-full mb-2 gap-2">
                      <div
                        className={`w-5 h-5 ${
                          selectedAddress === address.id ? "border-2 border-[#008AF6]" : "border"
                        }  rounded-full flex justify-center items-center`}
                      >
                        {selectedAddress === address.id ? (
                          <div className=" w-[14px] h-[14px] bg-[#008AF6] rounded-full"></div>
                        ) : null}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-normal text-[#333]">
                          <bdi>{address.name}</bdi>
                        </p>
                      </div>
                      {!address.by_phone && (
                        <BiPencil
                          className="w-5 h-5 text-[#999] cursor-pointer hover:text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAddress(address);
                          }}
                        />
                      )}
                    </div>
                    <div className="w-full flex justify-start">
                      <p className=" text-xs font-normal text-[#999] leading-6">
                        <bdi>{convertNumber(locale, address.text)}</bdi>
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {orderByPhoneStatus && (
                <div
                  className={`lg:w-full md:w-full sm:w-full xs:w-full w-full max-w-[480px] mx-auto mb-1 p-2 `}
                >
                  <div
                    className={`w-full min-h-[56px] cursor-pointer transition-all ease-in-out duration-150 ${
                      selectedAddress === "SendWithoutAddress"
                        ? " border-2 border-[#008AF6]"
                        : "border-2"
                    }  rounded-2xl px-5 flex items-center`}
                    onClick={() => {
                      setSelectedAddress("SendWithoutAddress");
                    }}
                  >
                    <div className="flex flex-wrap items-center w-full ">
                      <div className="flex-1">
                        <p className="text-sm font-normal text-[#333]">
                          {t("client_address_sendwithoutaddress")}
                        </p>
                      </div>
                      <div
                        className={`w-5 h-5 ${
                          selectedAddress === "SendWithoutAddress"
                            ? "border-2 border-[#008AF6]"
                            : "border"
                        }  rounded-full flex justify-center items-center`}
                      >
                        {selectedAddress === "SendWithoutAddress" ? (
                          <div className=" w-[14px] h-[14px] bg-[#008AF6] rounded-full"></div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-full mt-5">
            <div className="w-full flex flex-wrap justify-center">
              <Button
                size="slim"
                className="h-12 font-extrabold text-base rounded-full m-2 w-56"
                theme="lightBlue"
                onClick={handleOpenNewAddress}
              >
                {t("client_buttons_addaddress")}
              </Button>
              <Button
                isDisabled={!selectedAddress}
                size="slim"
                className=" h-12 font-extrabold text-base rounded-full m-2  w-56 disabled:bg-[#999] disabled:border-[#999]"
                onClick={() =>
                  selectedAddress === "SendWithoutAddress"
                    ? setOpenReceiverInfo(true)
                    : handelChangeAddress()
                }
              >
                {t("client_buttons_next")}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-full max-w-[480px] mt-32 m-auto">
            <div className=" flex flex-col items-center">
              <AddressesIcon stroke="#999" className="w-10 h-10" />
              <p className=" text-base text-[#999] font-normal text-center mt-5">
                {t("client_myaddresses_myaddresses_nodatamessage")}
              </p>
              <div className="w-full mt-14 mb-3">
                <Button
                  size="slim"
                  className="m-auto h-12 font-extrabold text-base rounded-full flex px-2 w-[200px]"
                  onClick={handleOpenNewAddress}
                >
                  {t("client_buttons_addaddress")}
                </Button>
              </div>
            </div>
            {orderByPhoneStatus ? (
              <div className="lg:w-full md:w-full sm:w-full xs:w-full w-full max-w-[480px] mx-auto mt-8  mb-1 p-2">
                <p className=" text-textLight font-normal text-xs w-full text-center">
                  {t("client_address_donthavetheaddressclickhere").replace("@", "")}
                  <span
                    className=" text-brandBlue px-1 cursor-pointer"
                    onClick={() => setOpenReceiverInfo(true)}
                  >
                    {t("client_address_clickhere")}
                  </span>
                </p>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default Address;
